import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import engageService, {
    DataGolTokenResponse,
    LoadStatus,
    ENGAGE_UNAUTHORIZED_STATE,
    ENGAGE_FAILED_STATE,
    UNAUTHORIZED_REDIRECT_DELAY_MS,
    getLoadStatusFromError,
} from '../../features/engage/engageService';
import { RootState } from '../../app/store';
import config from '../../app/config';
import { NoResultsFound } from '../../components/common/NoResultsFound';
import { forceLogoutToLogin } from '../../utils/auth/forceLogout';
import { useScript } from '../../hooks/useScript';
import { resolveActiveTenantCode } from '../../utils/tenantSession';
import './EngageDashboard.scss';

declare global {
    interface Window {
        DataGOL?: {
            init: (config: {
                elementId: string;
                appId: string;
                dashboardId: string;
                userToken: string;
                filters: [
                    {
                        name: "tenant",
                        value: string;
                    }
                ]
                onSessionExpire?: () => Promise<string>;
            }) => void;
        };
    }
}

const DASHBOARD_ELEMENT_ID = 'engage-dashboard';

export const EngageDashboard = () => {
    const location = useLocation();
    const [token, setToken] = useState<DataGolTokenResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [loadStatus, setLoadStatus] = useState<LoadStatus>('ok');
    const sessionExpireAttempts = useRef(0);
    const MAX_SESSION_EXPIRE_ATTEMPTS = 3;
    const isInitialized = useRef(false);
    const scriptStatus = useScript(config.dataGolEmbedScriptUrl);
    const user = useSelector<RootState, any>(
        (state) => state?.users?.user || null
    );
    const tenantCode = useMemo(() => resolveActiveTenantCode(user), [user]);
    const userLoading = useSelector<RootState, boolean>(
        (state) => state?.users?.loading ?? false
    );

    const { appId, dashboardId } = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return {
            appId: params.get('appId') || '',
            dashboardId: params.get('dashboardId') || '',
        };
    }, [location.search]);

    useEffect(() => {
        if (!appId || !dashboardId) {
            setLoadStatus('failed');
            setIsLoading(false);
            return;
        }

        // Wait for the Redux user to settle before deciding whether the tenant
        // is missing — otherwise we'd flash a "failed" state during the initial
        // retrieveUser fetch.
        if (userLoading) {
            return;
        }

        if (!tenantCode) {
            if (!user) {
                return;
            }

            setLoadStatus('failed');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setLoadStatus('ok');
        sessionExpireAttempts.current = 0;
        isInitialized.current = false;

        engageService.getDataGolToken({ tenantCode, dashboardId })
            .then((response) => {
                setToken(response.data);
                setLoadStatus('ok');
            })
            .catch((error) => {
                setLoadStatus(getLoadStatusFromError(error));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [appId, dashboardId, tenantCode, user, userLoading]);

    const handleSessionExpire = useCallback(async (): Promise<string> => {
        if (sessionExpireAttempts.current >= MAX_SESSION_EXPIRE_ATTEMPTS) {
            // Let DataGOL render its own error — do NOT surface our NoResultsFound.
            throw new Error('Maximum session refresh attempts reached.');
        }
        sessionExpireAttempts.current += 1;
        const response = await engageService.getDataGolToken({ tenantCode, dashboardId });
        return response.data.userToken;
    }, [tenantCode, dashboardId]);

    useEffect(() => {
        if (scriptStatus === 'error') {
            setLoadStatus('failed');
            return;
        }

        if (!token || scriptStatus !== 'ready') return;

        if (!window.DataGOL || typeof window.DataGOL.init !== 'function') {
            setLoadStatus('failed');
            return;
        }

        isInitialized.current = true;
        window.DataGOL.init({
            elementId: DASHBOARD_ELEMENT_ID,
            appId,
            dashboardId,
            userToken: token.userToken,
            filters: [
                {
                    name: "tenant",
                    value: tenantCode
                }
            ],
            onSessionExpire: handleSessionExpire,
        });
    }, [token, appId, dashboardId, handleSessionExpire, scriptStatus]);

    useEffect(() => {
        if (loadStatus !== 'unauthorized') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            forceLogoutToLogin();
        }, UNAUTHORIZED_REDIRECT_DELAY_MS);

        return () => window.clearTimeout(timeoutId);
    }, [loadStatus]);

    // Once DataGOL has initialised it owns its own error UI — hide ours.
    const showError = !isLoading && !userLoading && !isInitialized.current && loadStatus !== 'ok';
    const errorState = loadStatus === 'unauthorized' ? ENGAGE_UNAUTHORIZED_STATE : ENGAGE_FAILED_STATE;

    return (
        <div className="engage-dashboard-container">
            <div id={DASHBOARD_ELEMENT_ID} className="engage-dashboard-host" />
            {showError && (
                <NoResultsFound
                    message={errorState.message}
                    description={errorState.description}
                />
            )}
        </div>
    );
};
